import React, { useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import Badge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { isEmpty } from "lodash";
import { makeStyles, withStyles } from "tss-react/mui";

import DeleteModal from "@/components/DeleteModal";
import FormPaperIcon from "@/components/Icons/FormPaperIcon";
import NutricoachIcon from "@/components/Icons/NutricoachIcon";
import { graphql } from "@/gql";
import { MyFormFragment, OrderByDirection } from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";
import { displayError } from "@/utils/errors";
import { FragmentType } from "@/utils/type";

import OptionMenu from "./components/OptionMenu";
import SentFormClientMenu from "./components/SentFormsClientMenu";
import { formsPageQuery } from "../FormsPage";

const useStyles = makeStyles()({
  badge: {
    width: "100%",
    position: "relative",
    top: "8px",
  },
  image: {
    objectFit: "contain",
    width: "100%",
    height: "100%",
  },
  sort: {
    width: 182,
  },
});

interface Props {
  myForms: FragmentType<typeof myFormsFragment>[];
  loading: boolean;
  onPreviewForm: (data: MyFormFragment | null, isSending: boolean) => void;
  order: OrderByDirection;
  handleSort: (event: SelectChangeEvent) => void;
}

const StyledNutricoachLogo = withStyles(NutricoachIcon, {
  root: {
    maxHeight: 24,
    maxWidth: 24,
  },
});

const myFormsFragment = graphql(`
  fragment MyForm on Form {
    id
    title
    previewUrl
    thumbnailUrl
    pdfUrl
    createdAt
    updatedAt
    coach {
      id
      profile {
        id
        fullName
      }
    }
    sentTo {
      nodes {
        id
        email
        profile {
          id
          fullName
          avatarUrl
        }
      }
    }
  }
`);

const coachViewerIdQuery = graphql(`
  query CoachViewerId {
    coachViewer {
      id
    }
  }
`);

const deleteFormMutation = graphql(`
  mutation DeleteForm($input: DeleteFormInput!) {
    deleteForm(input: $input) {
      form {
        id
      }
    }
  }
`);

const MyForms: React.FC<Props> = ({
  myForms,
  loading,
  onPreviewForm,
  order,
  handleSort,
}) => {
  const { classes } = useStyles();
  const deleteModal = useDialogState();
  const [currentForm, setCurrentForm] = useState<MyFormFragment | null>(null);
  const [deleteForm] = useMutation(deleteFormMutation);
  const snackbar = useGlobalSnackbar();

  const { data } = useQuery(coachViewerIdQuery);
  const coachId = data?.coachViewer.id;
  const handleMenuClick = (type: string, data: MyFormFragment): void => {
    switch (type) {
      case "delete":
        setCurrentForm(data);
        return deleteModal.show();
      case "preview":
        return onPreviewForm(data, false);
      case "send":
        return onPreviewForm(data, true);
      default:
        return;
    }
  };

  const handleDeleteForm = async () => {
    if (currentForm == null) return;
    try {
      const result = await deleteForm({
        variables: { input: { id: currentForm.id } },
        refetchQueries: [
          {
            query: formsPageQuery,
            variables: { title: "", orderByTitle: order },
          },
        ],
      });
      if (result.data) snackbar.open("Form successfully deleted!");
    } catch (error) {
      snackbar.open(displayError(error, "Unable to delete form"));
    }
  };

  const _mapForm = (form: MyFormFragment) => {
    return (
      <Grid key={form.id} item xs={12} md={4} lg={2}>
        <Badge
          className={classes.badge}
          badgeContent={
            <>{coachId != form.coach.id && <StyledNutricoachLogo />}</>
          }
        />
        <Box
          border={`1px solid ${colors.grayLight300}`}
          height="275px"
          p="12px"
        >
          <Box height="190px">
            <img className={classes.image} src={form.thumbnailUrl} />
          </Box>
          <Box height="12px" />
          <Box alignItems="center" display="flex" width="100%">
            <Box display="flex" flex={1} flexDirection="column">
              <Typography fontSize="12px" fontWeight={700} color="#363E45">
                {form.title}
              </Typography>
              <Typography fontSize="10px" fontWeight={700} color="#7D8797">
                {format({ datetime: form?.createdAt }, "dd MMM, yyyy")}
              </Typography>
              <SentFormClientMenu data={form} />
            </Box>
            <OptionMenu
              coachId={coachId ?? ""}
              ownerId={form.coach.id}
              formId={form.id}
              onClickMenu={(type) => handleMenuClick(type, form)}
            />
          </Box>
        </Box>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        padding={14}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box bgcolor={colors.theme.system.white} padding="24px">
      <DeleteModal
        visible={deleteModal.shown}
        onClose={deleteModal.hide}
        title="Delete form"
        message="Are you sure you want to delete this form?"
        onDelete={handleDeleteForm}
      />
      <Box display="flex" flexDirection="row" justifyContent="flex-end">
        <Select value={order} onChange={handleSort}>
          <MenuItem value={OrderByDirection.Asc}>Ascending</MenuItem>
          <MenuItem value={OrderByDirection.Desc}>Descending</MenuItem>
        </Select>
      </Box>
      <Box height="16px" />
      <Grid container spacing="16px">
        {myForms && myForms.map(_mapForm)}
      </Grid>
      {isEmpty(myForms) && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py="64px"
        >
          <FormPaperIcon />
          <Typography color="#AAB0BB" fontWeight={700}>
            No Forms yet
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MyForms;
